import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isRestrictedVendor } from '../utils/permissions';
import { vendorService } from '../services/dataService';
import {
  BarList,
  KpiCard,
  RawPayload,
  StatusBreakdown,
  StatusPill,
  toLabel,
} from '../components/DetailDashboard';
import styles from './assets/css/DetailDashboard.module.css';

const OPERATIONAL_ORDER = ['operational', 'maintenance', 'non_operational', 'unknown'];

const VendorDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const canOpenFacilities = !isRestrictedVendor(user);
  const [vendor, setVendor] = useState(null);
  const [summary, setSummary] = useState(null);
  const [facilityFilter, setFacilityFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadVendor = async () => {
      setLoading(true);
      setError('');
      try {
        const [vendorData, summaryData] = await Promise.all([
          vendorService.getById(id),
          vendorService.getSummary(id),
        ]);
        if (!cancelled) {
          setVendor(vendorData);
          setSummary(summaryData);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || err?.detail || 'Failed to load vendor details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (id) loadVendor();
    return () => { cancelled = true; };
  }, [id]);

  const visibleFacilities = useMemo(() => {
    const term = facilityFilter.trim().toLowerCase();
    const facilities = summary?.facilities || [];
    if (!term) return facilities;
    return facilities.filter((facility) => (
      [facility.name, facility.id, facility.county].some((value) => String(value || '').toLowerCase().includes(term))
    ));
  }, [summary, facilityFilter]);

  const totalMachines = summary?.total_machines || 0;
  const operationalMachines = summary?.operational_status_counts?.operational || 0;
  const modalityCount = Object.keys(summary?.modality_counts || {}).length;

  return (
    <div className={styles.page}>
      <Link className={styles.backLink} to="/vendors">← Back to Vendors</Link>

      {loading ? <div className={styles.card}>Loading vendor details...</div> : null}
      {!loading && error ? <div className={styles.error}>{error}</div> : null}

      {!loading && !error && vendor && summary ? (
        <>
          <header className={styles.hero}>
            <div className={styles.heroMain}>
              <div className={styles.heroTitle}>
                <h1>{vendor.name}</h1>
                <StatusPill value={vendor.lifecycle_state} />
              </div>
              <div className={styles.codeRow}>
                {vendor.vendor_alpha_code ? <span className={styles.code}>Vendor {vendor.vendor_alpha_code}</span> : null}
                <span className={styles.code}>DHA {vendor.dha_vendor_code}</span>
                <span className={styles.code}>SHA {vendor.sha_vendor_code}</span>
              </div>
            </div>
            <div className={styles.heroContact}>
              {vendor.email ? <a href={`mailto:${vendor.email}`}>{vendor.email}</a> : null}
              {vendor.phone ? <span>{vendor.phone}</span> : null}
              {vendor.website ? (
                <a href={vendor.website} target="_blank" rel="noopener noreferrer">{vendor.website}</a>
              ) : null}
            </div>
          </header>

          <section className={styles.kpiGrid}>
            <KpiCard label="Facilities" value={summary.total_facilities} hint="with this vendor's machines" />
            <KpiCard label="Machines" value={totalMachines} hint={`${modalityCount} modalit${modalityCount === 1 ? 'y' : 'ies'}`} />
            <KpiCard
              label="Operational"
              value={`${totalMachines ? Math.round((operationalMachines / totalMachines) * 100) : 0}%`}
              hint={`${operationalMachines} of ${totalMachines} machines`}
            />
            <KpiCard
              label="Linked to Gateway"
              value={`${summary.linked_machines} / ${totalMachines}`}
              hint="approved worklist connection"
            />
            <KpiCard label="Requests" value={summary.total_requests} hint="routed to this vendor's machines" />
          </section>

          <section className={styles.chartGrid}>
            <div className={styles.card}>
              <h2>Machines by Modality</h2>
              <BarList counts={summary.modality_counts} emptyText="No machines registered for this vendor." />
            </div>

            <div className={styles.card}>
              <h2>Operational Status</h2>
              <StatusBreakdown
                counts={summary.operational_status_counts}
                order={OPERATIONAL_ORDER}
                label="Operational status distribution"
                emptyText="No operational status data available."
              />
            </div>
          </section>

          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>Facilities and Machines</h2>
              {summary.facilities.length > 5 ? (
                <input
                  type="search"
                  className={styles.filterInput}
                  placeholder="Filter by facility, FID or county"
                  value={facilityFilter}
                  onChange={(e) => setFacilityFilter(e.target.value)}
                />
              ) : null}
            </div>
            {visibleFacilities.length ? (
              <div className={styles.facilityList}>
                {visibleFacilities.map((facility) => (
                  <details className={styles.facility} key={facility.id}>
                    <summary>
                      <span className={styles.facilityName}>
                        <strong>{facility.name}</strong>
                        <span className={styles.muted}>
                          {facility.id}{facility.county ? ` · ${facility.county}` : ''}
                        </span>
                      </span>
                      <span className={styles.countBadge}>
                        {facility.machines.length} machine{facility.machines.length === 1 ? '' : 's'}
                      </span>
                    </summary>
                    <div className={styles.tableWrap}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>Machine</th>
                            <th>Modality</th>
                            <th>AE Title</th>
                            <th>Lifecycle</th>
                            <th>Operational</th>
                            <th>Gateway</th>
                          </tr>
                        </thead>
                        <tbody>
                          {facility.machines.map((machine) => (
                            <tr key={machine.id}>
                              <td>
                                <Link to={`/equipment/${machine.id}`}>{machine.name}</Link>
                                <span className={styles.subText}>{machine.asset_id}</span>
                              </td>
                              <td>{machine.modality || '—'}</td>
                              <td><code>{machine.dicom_aet || '—'}</code></td>
                              <td><StatusPill value={machine.lifecycle_state} /></td>
                              <td><StatusPill value={machine.operational_status} /></td>
                              <td>
                                <span className={`${styles.pill} ${machine.is_linked ? styles.toneGood : styles.toneNeutral}`}>
                                  {machine.is_linked ? 'Linked' : 'Not linked'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {canOpenFacilities && facility.id !== 'unassigned' ? (
                      <Link className={styles.facilityLink} to={`/facilities/${facility.id}`}>
                        Open facility →
                      </Link>
                    ) : null}
                  </details>
                ))}
              </div>
            ) : (
              <p className={styles.muted}>
                {summary.facilities.length ? 'No facilities match the filter.' : 'No facilities linked to this vendor yet.'}
              </p>
            )}
          </section>

          <section className={styles.profileGrid}>
            <div className={styles.card}>
              <h2>Vendor Profile</h2>
              <dl className={styles.fields}>
                <div><dt>Vendor ID</dt><dd><code>{vendor.id}</code></dd></div>
                <div><dt>Country</dt><dd>{vendor.country || '—'}</dd></div>
                <div><dt>Address</dt><dd>{vendor.address || '—'}</dd></div>
                <div><dt>Is Vendor</dt><dd>{vendor.is_vendor ? 'Yes' : 'No'}</dd></div>
                <div className={styles.fieldWide}><dt>Description</dt><dd>{vendor.description || '—'}</dd></div>
              </dl>
            </div>

            <div className={styles.card}>
              <h2>Supported Modalities</h2>
              {vendor.modalities?.length ? (
                <div className={styles.chips}>
                  {vendor.modalities.map((modality) => (
                    <span className={styles.chip} key={modality.id || modality.code} title={modality.name}>
                      <strong>{modality.code}</strong> {modality.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className={styles.muted}>No modalities assigned.</p>
              )}

              <h2 className={styles.subHeading}>Contacts</h2>
              {vendor.contacts?.length ? (
                <ul className={styles.contactList}>
                  {vendor.contacts.map((contact) => (
                    <li key={contact.id}>
                      <strong>
                        {`${contact.first_name} ${contact.last_name}`}
                        {contact.is_primary ? <span className={styles.primaryTag}>Primary</span> : null}
                      </strong>
                      <span className={styles.muted}>
                        {[toLabel(contact.contact_type), contact.title].filter(Boolean).join(' · ')}
                      </span>
                      <span>{[contact.email, contact.phone].filter(Boolean).join(' · ') || '—'}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.muted}>No contacts recorded.</p>
              )}
            </div>
          </section>

          <RawPayload data={vendor} />
        </>
      ) : null}
    </div>
  );
};

export default VendorDetailPage;
